"use client"

import type { FullTranslation } from "../../lib/i18n"
import { useTr } from "../../lib/use-tr"
import { Reveal } from "../reveal"
import {
  BarChart3,
  Check,
  Facebook,
  Linkedin,
  Mail,
  MapPin,
  Phone,
  Puzzle,
  Send,
  Share2,
  Star,
  Target,
} from "lucide-react"
import { ReactNode, useEffect, useRef, useState } from "react"

import { cn } from "@/lib/utils"

const ROTATION_MS = 5000

const PLUGINS_T = {
  eyebrow: { FR: "Plugins", EN: "Plugins" },
  headline: {
    FR: "Activez seulement ce dont vous avez besoin.",
    EN: "Enable only what you need.",
  },
  subhead: {
    FR: "Chaque plugin ajoute une dimension à vos leads. Coût et richesse s'adaptent à la campagne.",
    EN: "Each plugin adds a dimension to your leads. Cost and depth adapt to the campaign.",
  },
  p_seo_name: { FR: "Website Quality Score", EN: "Website Quality Score" },
  p_seo_body: {
    FR: "Audit SEO agence-grade sur 10 critères : performance, mobile, SSL, schema, contenu, accessibilité.",
    EN: "Agency-grade SEO audit across 10 criteria: performance, mobile, SSL, schema, content, accessibility.",
  },
  p_seo_meta: { FR: "+0.5x", EN: "+0.5x" },
  p_seo_demo_score: { FR: "7.4/10", EN: "7.4/10" },
  p_seo_demo_label: {
    FR: "Score moyen sur cette campagne",
    EN: "Average score on this campaign",
  },
  p_seo_demo_header: {
    FR: "Audit SEO · 10 critères",
    EN: "SEO audit · 10 criteria",
  },
  p_seo_demo_crit1: { FR: "Performance", EN: "Performance" },
  p_seo_demo_crit2: { FR: "Mobile-friendly", EN: "Mobile-friendly" },
  p_seo_demo_crit3: { FR: "SSL & sécurité", EN: "SSL & security" },
  p_seo_demo_crit4: { FR: "Schema.org", EN: "Schema.org" },
  p_seo_demo_score_row: { FR: "Score global", EN: "Overall score" },
  p_seo_demo_outof: { FR: "/100", EN: "/100" },

  p_email_demo_header: { FR: "Emails vérifiés", EN: "Verified emails" },
  p_email_demo_role1: { FR: "Direction", EN: "Management" },
  p_email_demo_role2: { FR: "Marketing", EN: "Marketing" },
  p_email_demo_role3: { FR: "Contact générique", EN: "Generic contact" },

  p_phone_demo_header: { FR: "Numéros sourcés", EN: "Sourced numbers" },

  p_ratings_demo_header: { FR: "Sources agrégées", EN: "Aggregated sources" },

  p_ads_demo_header: { FR: "Détection d'ads", EN: "Ad detection" },

  p_socials_demo_header: { FR: "Profils officiels", EN: "Official profiles" },

  p_mail_demo_header: { FR: "Séquence 3 étapes", EN: "3-step sequence" },
  p_mail_demo_pushed: {
    FR: "Poussé dans Lemlist",
    EN: "Pushed to Lemlist",
  },

  p_email_name: { FR: "Email enrichment", EN: "Email enrichment" },
  p_email_body: {
    FR: "Emails vérifiés extraits du site et de sources publiques.",
    EN: "Verified emails extracted from the website and public sources.",
  },
  p_email_meta: { FR: "Inclus", EN: "Included" },
  p_email_demo_prospect: { FR: "Contact", EN: "Contact" },
  p_email_demo_status: { FR: "Vérifié", EN: "Verified" },

  p_phone_name: { FR: "Phone enrichment", EN: "Phone enrichment" },
  p_phone_body: {
    FR: "Numéros directs, fixes et mobiles, sourcés et nettoyés.",
    EN: "Direct lines, landlines and mobiles, sourced and cleaned.",
  },
  p_phone_meta: { FR: "+0.2x", EN: "+0.2x" },
  p_phone_demo_mobile: { FR: "Mobile", EN: "Mobile" },
  p_phone_demo_landline: { FR: "Fixe", EN: "Landline" },

  p_ratings_name: { FR: "Ratings multi-sources", EN: "Multi-source ratings" },
  p_ratings_body: {
    FR: "Google, Trustpilot et annuaires locaux agrégés en une note unique avec sources.",
    EN: "Google, Trustpilot and local directories aggregated into a single score with sources.",
  },
  p_ratings_meta: { FR: "+0.2x", EN: "+0.2x" },
  p_ratings_demo_aggregate: { FR: "Note agrégée", EN: "Aggregated score" },
  p_ratings_demo_score: { FR: "4.5 / 5", EN: "4.5 / 5" },

  p_ads_name: { FR: "Ad signals", EN: "Ad signals" },
  p_ads_body: {
    FR: "Détection Google Ads et Meta Ads avec preuves cliquables.",
    EN: "Google Ads and Meta Ads detection with clickable proof.",
  },
  p_ads_meta: { FR: "+0.3x", EN: "+0.3x" },
  p_ads_demo_active: { FR: "Actif", EN: "Active" },
  p_ads_demo_proofs: { FR: "preuves", EN: "proofs" },

  p_socials_name: { FR: "Socials", EN: "Socials" },
  p_socials_body: {
    FR: "Comptes LinkedIn et Facebook officiels du prospect.",
    EN: "The prospect's official LinkedIn and Facebook accounts.",
  },
  p_socials_meta: { FR: "+0.2x", EN: "+0.2x" },
  p_socials_demo_followers: { FR: "abonnés", EN: "followers" },

  p_mail_name: { FR: "Mailing copy", EN: "Mailing copy" },
  p_mail_body: {
    FR: "Séquence cold email 3 étapes sur-mesure, poussée dans votre Lemlist.",
    EN: "A tailored 3-step cold email sequence, pushed to your Lemlist.",
  },
  p_mail_meta: { FR: "+0.4x", EN: "+0.4x" },
  p_mail_demo_step1: {
    FR: "Accroche personnalisée",
    EN: "Personalized opener",
  },
  p_mail_demo_step2: { FR: "Relance avec preuve", EN: "Follow-up with proof" },
  p_mail_demo_step3: { FR: "Break-up CTA", EN: "Break-up CTA" },
  p_mail_demo_day: { FR: "J", EN: "D" },
} satisfies FullTranslation

type PluginKey =
  | "p_seo"
  | "p_email"
  | "p_phone"
  | "p_ratings"
  | "p_ads"
  | "p_socials"
  | "p_mail"

type PluginEntry = {
  key: PluginKey
  Icon: typeof BarChart3
  tint: string
  iconColor: string
}

const PLUGINS: ReadonlyArray<PluginEntry> = [
  {
    key: "p_seo",
    Icon: BarChart3,
    tint: "bg-[var(--chart-2)]/12",
    iconColor: "text-[var(--chart-2)]",
  },
  {
    key: "p_email",
    Icon: Mail,
    tint: "bg-[var(--chart-1)]/12",
    iconColor: "text-[var(--chart-1)]",
  },
  {
    key: "p_phone",
    Icon: Phone,
    tint: "bg-[var(--chart-2)]/12",
    iconColor: "text-[var(--chart-2)]",
  },
  {
    key: "p_ratings",
    Icon: Star,
    tint: "bg-[var(--chart-3)]/12",
    iconColor: "text-[var(--chart-3)]",
  },
  {
    key: "p_ads",
    Icon: Target,
    tint: "bg-[var(--chart-4)]/12",
    iconColor: "text-[var(--chart-4)]",
  },
  {
    key: "p_socials",
    Icon: Share2,
    tint: "bg-[var(--chart-5)]/12",
    iconColor: "text-[var(--chart-5)]",
  },
  {
    key: "p_mail",
    Icon: MapPin,
    tint: "bg-[var(--chart-1)]/12",
    iconColor: "text-[var(--chart-1)]",
  },
]

export function Plugins() {
  const tr = useTr(PLUGINS_T)
  const [activeIndex, setActiveIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const [visible, setVisible] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Rotating off screen re-renders the whole section every 5 s for nothing.
  useEffect(() => {
    const element = containerRef.current
    if (!element) return
    const observer = new IntersectionObserver(([entry]) =>
      setVisible(entry.isIntersecting),
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (paused || !visible) return
    const id = setInterval(() => {
      setActiveIndex((i) => (i + 1) % PLUGINS.length)
    }, ROTATION_MS)
    return () => clearInterval(id)
  }, [paused, visible])

  const active = PLUGINS[activeIndex]

  return (
    <section
      id="plugins"
      aria-labelledby="plugins-heading"
      className="scroll-mt-28 py-16 sm:py-20 md:py-24 lg:scroll-mt-40"
    >
      <Reveal className="sm:container">
        <div className="mb-12 max-w-2xl text-left">
          <div className="bg-card/60 text-muted-foreground mb-5 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium backdrop-blur-sm">
            <Puzzle className="text-primary-cta size-3.5" aria-hidden />
            <span>{tr("eyebrow")}</span>
          </div>
          <h2
            id="plugins-heading"
            className="text-foreground font-display leading-display tracking-dense text-3xl font-semibold sm:text-4xl md:text-5xl"
          >
            {tr("headline")}
          </h2>
          <p className="text-muted-foreground text-md mt-4 max-w-md leading-relaxed sm:text-base">
            {tr("subhead")}
          </p>
        </div>

        <div
          ref={containerRef}
          className="grid grid-cols-1 gap-4 lg:grid-cols-5"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* Active demo */}
          <article
            key={active.key}
            className="bg-card ring-border/60 plugin-card relative flex min-h-90 flex-col gap-5 overflow-hidden rounded-2xl border p-6 ring-1 sm:p-8 lg:col-span-3"
          >
            <div
              className="plugin-stagger flex items-start justify-between gap-3"
              style={{ "--d": "0ms" } as React.CSSProperties}
            >
              <div
                className={`plugin-icon-pop inline-flex size-10 items-center justify-center rounded-xl ${active.tint}`}
              >
                <active.Icon
                  className={`size-5 ${active.iconColor}`}
                  aria-hidden
                />
              </div>
            </div>
            <div
              className="plugin-stagger"
              style={{ "--d": "80ms" } as React.CSSProperties}
            >
              <h3 className="text-foreground text-lg font-semibold tracking-tight sm:text-xl">
                {tr(`${active.key}_name`)}
              </h3>
              <p className="text-muted-foreground mt-1.5 max-w-md text-sm leading-relaxed">
                {tr(`${active.key}_body`)}
              </p>
            </div>

            <div
              className="plugin-stagger mt-auto"
              style={{ "--d": "160ms" } as React.CSSProperties}
            >
              <PluginDemo pluginKey={active.key} />
            </div>
          </article>

          {/* Plugin list */}
          <ul className="flex flex-col gap-2 lg:col-span-2">
            {PLUGINS.map((p, i) => {
              const isActive = i === activeIndex
              return (
                <li key={p.key}>
                  <button
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => setActiveIndex(i)}
                    className={`group relative flex w-full items-center gap-3 overflow-hidden rounded-xl border px-4 py-3 text-left transition-all duration-300 ${
                      isActive
                        ? "bg-card border-primary-cta/40 ring-primary-cta/10 plugin-active-glow scale-[1.01] ring-2"
                        : "bg-card/40 border-border/60 hover:bg-card hover:scale-[1.005]"
                    }`}
                  >
                    <div
                      className={`inline-flex size-8 shrink-0 items-center justify-center rounded-lg transition-transform duration-300 ${p.tint} ${
                        isActive ? "scale-110" : "group-hover:scale-105"
                      }`}
                    >
                      <p.Icon className={`size-4 ${p.iconColor}`} aria-hidden />
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="text-foreground block truncate text-sm font-medium">
                        {tr(`${p.key}_name`)}
                      </span>
                      <p className="text-muted-foreground mt-0.5 line-clamp-1 text-xs">
                        {tr(`${p.key}_body`)}
                      </p>
                    </div>
                    {isActive && !paused && (
                      <span
                        key={activeIndex}
                        className="bg-primary-cta/60 absolute right-0 bottom-0 h-0.5 rounded-full"
                        style={{
                          animation: `plugin-progress ${ROTATION_MS}ms linear forwards`,
                        }}
                        aria-hidden
                      />
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      </Reveal>
    </section>
  )
}

function DemoCard({
  children,
  headerIcon: HeaderIcon,
  headerLabel,
  headerRight,
}: {
  children: ReactNode
  headerIcon?: typeof BarChart3
  headerLabel?: string
  headerRight?: ReactNode
}) {
  return (
    <div className="bg-background ring-border/60 w-full rounded-xl p-4 shadow-sm ring-1">
      {(headerLabel || headerRight) && (
        <div className="text-muted-foreground/80 mb-3 flex items-center justify-between text-xs">
          <span className="inline-flex items-center gap-2 font-mono tracking-wide uppercase">
            {HeaderIcon ? (
              <HeaderIcon className="size-3.5" aria-hidden />
            ) : null}
            {headerLabel}
          </span>
          {headerRight}
        </div>
      )}
      {children}
    </div>
  )
}

function PluginDemo({ pluginKey }: { pluginKey: PluginKey }) {
  const tr = useTr(PLUGINS_T)

  if (pluginKey === "p_seo") {
    const criteria = [
      { key: "p_seo_demo_crit1", score: "92" },
      { key: "p_seo_demo_crit2", score: "88" },
      { key: "p_seo_demo_crit3", score: "100" },
      { key: "p_seo_demo_crit4", score: "60" },
    ] as const
    return (
      <DemoCard
        headerIcon={BarChart3}
        headerLabel={tr("p_seo_demo_header")}
        headerRight={
          <span className="text-primary-cta-text text-2xs font-mono font-semibold">
            {tr("p_seo_demo_score")}
          </span>
        }
      >
        <ul className="flex flex-col gap-2">
          {criteria.map((c, i) => (
            <li
              key={c.key}
              className="plugin-fade-up text-2sm flex items-center justify-between gap-3"
              style={{ "--d": `${i * 80}ms` } as React.CSSProperties}
            >
              <span className="text-foreground flex items-center gap-2">
                <span className="bg-primary-cta text-primary-cta-foreground inline-flex size-4 items-center justify-center rounded-full">
                  <Check className="size-2.5" aria-hidden />
                </span>
                {tr(c.key)}
              </span>
              <span className="text-muted-foreground text-2xs font-mono">
                {c.score}
                {tr("p_seo_demo_outof")}
              </span>
            </li>
          ))}
        </ul>
      </DemoCard>
    )
  }

  if (pluginKey === "p_email") {
    const contacts = [
      { email: "marie.durand@acme.fr", role: "p_email_demo_role1" },
      { email: "thomas.b@acme.fr", role: "p_email_demo_role2" },
      { email: "contact@acme.fr", role: "p_email_demo_role3" },
    ] as const
    return (
      <DemoCard
        headerIcon={Mail}
        headerLabel={tr("p_email_demo_header")}
        headerRight={
          <span className="text-primary-cta-text text-2xs inline-flex items-center gap-1">
            <Check className="size-3" aria-hidden />
            {tr("p_email_demo_status")}
          </span>
        }
      >
        <ul className="flex flex-col gap-1.5">
          {contacts.map((c, i) => (
            <li
              key={c.email}
              className="plugin-fade-up border-border/60 flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
              style={{ "--d": `${i * 100}ms` } as React.CSSProperties}
            >
              <div className="min-w-0">
                <div className="text-foreground truncate font-mono text-xs">
                  {c.email}
                </div>
                <div className="text-muted-foreground text-2xs">
                  {tr(c.role)}
                </div>
              </div>
              <span className="bg-primary-cta inline-flex size-4 shrink-0 items-center justify-center rounded-full">
                <Check
                  className="text-primary-cta-foreground size-2.5"
                  aria-hidden
                />
              </span>
            </li>
          ))}
        </ul>
      </DemoCard>
    )
  }

  if (pluginKey === "p_phone") {
    const phones = [
      {
        number: "+33 6 24 18 92 04",
        type: tr("p_phone_demo_mobile"),
        primary: true,
      },
      {
        number: "+33 1 42 88 15 30",
        type: tr("p_phone_demo_landline"),
        primary: false,
      },
      {
        number: "+33 6 71 03 44 87",
        type: tr("p_phone_demo_mobile"),
        primary: false,
      },
    ]
    return (
      <DemoCard headerIcon={Phone} headerLabel={tr("p_phone_demo_header")}>
        <ul className="flex flex-col gap-1.5">
          {phones.map((p, i) => (
            <li
              key={p.number}
              className={cn(
                "plugin-fade-up flex items-center justify-between gap-3 rounded-lg border px-3 py-2",
                p.primary
                  ? "border-primary-cta/30 bg-primary-cta/5"
                  : "border-border/60",
              )}
              style={{ "--d": `${i * 100}ms` } as React.CSSProperties}
            >
              <span className="text-foreground text-2sm font-mono">
                {p.number}
              </span>
              <span
                className={cn(
                  "text-3xs rounded-full px-2 py-0.5 font-medium ring-1",
                  p.primary
                    ? "bg-primary-cta/10 text-primary-cta-text ring-primary-cta/30"
                    : "bg-card text-muted-foreground ring-border/60",
                )}
              >
                {p.type}
              </span>
            </li>
          ))}
        </ul>
      </DemoCard>
    )
  }

  if (pluginKey === "p_ratings") {
    const sources = [
      { name: "Google", score: 4.6 },
      { name: "Trustpilot", score: 4.3 },
      { name: "PagesJaunes", score: 4.5 },
    ]
    return (
      <DemoCard
        headerIcon={Star}
        headerLabel={tr("p_ratings_demo_header")}
        headerRight={
          <span className="text-primary-cta-text text-2xs inline-flex items-center gap-1 font-mono font-semibold">
            <Star className="size-3 fill-current" aria-hidden />
            {tr("p_ratings_demo_score")}
          </span>
        }
      >
        <ul className="flex flex-col gap-1.5">
          {sources.map((s, i) => (
            <li
              key={s.name}
              className="plugin-fade-up border-border/60 text-2sm flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
              style={{ "--d": `${i * 100}ms` } as React.CSSProperties}
            >
              <span className="text-foreground">{s.name}</span>
              <span className="text-muted-foreground inline-flex items-center gap-1 font-mono text-xs">
                <Star className="text-primary-cta size-3" aria-hidden />
                {s.score.toFixed(1)}
              </span>
            </li>
          ))}
        </ul>
      </DemoCard>
    )
  }

  if (pluginKey === "p_ads") {
    const platforms = [
      { letter: "G", name: "Google Ads", count: 12 },
      { letter: "M", name: "Meta Ads", count: 4 },
    ]
    return (
      <DemoCard headerIcon={Target} headerLabel={tr("p_ads_demo_header")}>
        <ul className="flex flex-col gap-1.5">
          {platforms.map((p, i) => (
            <li
              key={p.letter}
              className="plugin-fade-up border-border/60 flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
              style={{ "--d": `${i * 120}ms` } as React.CSSProperties}
            >
              <div className="flex items-center gap-3">
                <span className="bg-muted text-foreground text-2xs inline-flex size-6 items-center justify-center rounded-md font-mono font-semibold">
                  {p.letter}
                </span>
                <div>
                  <div className="text-foreground text-2sm font-medium">
                    {p.name}
                  </div>
                  <div className="text-muted-foreground text-2xs">
                    {p.count} {tr("p_ads_demo_proofs")}
                  </div>
                </div>
              </div>
              <span className="bg-primary-cta/10 text-primary-cta-text ring-primary-cta/30 text-3xs inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ring-1">
                <span className="bg-primary-cta size-1.5 animate-pulse rounded-full" />
                {tr("p_ads_demo_active")}
              </span>
            </li>
          ))}
        </ul>
      </DemoCard>
    )
  }

  if (pluginKey === "p_socials") {
    const profiles = [
      { Icon: Linkedin, handle: "/company/acme", followers: "2 412" },
      { Icon: Facebook, handle: "/acme.officiel", followers: "1 058" },
    ]
    return (
      <DemoCard headerIcon={Share2} headerLabel={tr("p_socials_demo_header")}>
        <ul className="flex flex-col gap-1.5">
          {profiles.map((p, i) => (
            <li
              key={p.handle}
              className="plugin-fade-up border-border/60 flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
              style={{ "--d": `${i * 120}ms` } as React.CSSProperties}
            >
              <div className="flex items-center gap-3">
                <p.Icon className="text-primary-cta size-4" aria-hidden />
                <span className="text-foreground font-mono text-xs">
                  {p.handle}
                </span>
              </div>
              <span className="text-muted-foreground text-2xs font-mono">
                {p.followers} {tr("p_socials_demo_followers")}
              </span>
            </li>
          ))}
        </ul>
      </DemoCard>
    )
  }

  if (pluginKey === "p_mail") {
    const dayPrefix = tr("p_mail_demo_day")
    const steps = [
      {
        day: `${dayPrefix}+0`,
        label: tr("p_mail_demo_step1"),
        emphasis: true,
      },
      {
        day: `${dayPrefix}+3`,
        label: tr("p_mail_demo_step2"),
        emphasis: false,
      },
      {
        day: `${dayPrefix}+7`,
        label: tr("p_mail_demo_step3"),
        emphasis: false,
      },
    ]
    return (
      <DemoCard
        headerIcon={Send}
        headerLabel={tr("p_mail_demo_header")}
        headerRight={
          <span className="text-primary-cta-text text-2xs inline-flex items-center gap-1">
            <Send className="size-3" aria-hidden />
            {tr("p_mail_demo_pushed")}
          </span>
        }
      >
        <ol className="flex flex-col gap-2">
          {steps.map((s, i) => (
            <li
              key={s.day}
              className={cn(
                "plugin-fade-up flex items-center gap-3 rounded-lg border px-3 py-2.5",
                s.emphasis
                  ? "border-primary-cta/30 bg-primary-cta/5"
                  : "border-border/60",
              )}
              style={{ "--d": `${i * 140}ms` } as React.CSSProperties}
            >
              <span
                className={cn(
                  "text-2xs inline-flex size-6 shrink-0 items-center justify-center rounded-md font-mono font-medium",
                  s.emphasis
                    ? "bg-primary-cta text-primary-cta-foreground"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {i + 1}
              </span>
              <span className="text-foreground text-2sm">
                {s.day} · {s.label}
              </span>
            </li>
          ))}
        </ol>
      </DemoCard>
    )
  }

  return null
}
